import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import CheckoutSettings from '../types/CheckoutSettings';
import Price from '../types/Price';

// This handles setting all the form values that are used by the different checkout components and
// inputs.
const useCheckout = (): void => {
  const { setValue, watch } = useFormContext();

  // The specific checkout hook should set these after fetching the data from the server for the
  // checkout. Then this can use them to setup the checkout configuration as expected.
  const price: Price | undefined = watch('price');
  const checkoutSettings: CheckoutSettings | undefined = watch('checkoutSettings');

  // set static settings and default dynamic values -- static settings don't change unless you are
  // reloading an entirely new checkout page. These should come from the member, site, or checkout
  // settings data that's loaded from the server (and represents the configuration for this
  // checkout).
  useEffect(() => {
    if (price && checkoutSettings) {
      if (checkoutSettings.stripePublishableKey && checkoutSettings.stripeAccountId) {
        loadStripe(checkoutSettings.stripePublishableKey, {
          betas: ['elements_enable_deferred_intent_beta_1'],
          stripeAccount: checkoutSettings.stripeAccountId,
        }).then((stripe: Stripe | null) => setValue('stripe', stripe));
      }

      setValue('currency', price.currency);
      setValue('addressRequired', checkoutSettings.addressRequired);
      setValue('nameRequired', checkoutSettings.nameRequired);
      setValue('phoneRequired', checkoutSettings.phoneNumberRequired);
      setValue('paymentMethods', checkoutSettings.paymentMethods);
      setValue('additionalPaymentMethods', checkoutSettings.additionalPaymentMethods);
      setValue('vendorAccountId', checkoutSettings.vendorAccountId);

      // default dynamic values
      setValue('attemptReloadWithCardsOnly', false);
      setValue('amountPriceInCents', 0);
      setValue('dueNowPriceInCents', 0);
      setValue('email', undefined);
      setValue('name', undefined);
      setValue('phone', undefined);
      setValue('address', undefined); // stores all parts of an address -- see AddressInput
    }
  }, [price, checkoutSettings]);

  // set dynamic values that change as the user interacts with the
  const dueNowPriceInCents: number | undefined = watch('dueNowPriceInCents');

  useEffect(() => {
    if (!price) {
      return;
    }
    // TODO: add order bump, tax, coupon, and quantity handling here -- see popup checkout logic in
    // usePopupCheckout
    setValue('dueNowPriceInCents', price.amount);
  }, [price]);

  useEffect(() => {
    // TODO: add order bump handling -- see popup checkout logic in usePopupCheckout
    setValue('amountPriceInCents', dueNowPriceInCents);
  }, [dueNowPriceInCents]);
};

export default useCheckout;
